import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../pages/AuthPage";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const EMPTY = { current: "", password: "", confirm: "" };

export const AccountPassword = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasPassword = !!user?.has_password;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/set-password", { password: form.password, current_password: hasPassword ? form.current : null });
      setUser({ ...user, has_password: true });
      setForm(EMPTY);
      setOpen(false);
      toast.success(hasPassword ? "Password updated" : "Password added — you can now sign in with email + password");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white px-5 py-4 mb-10" data-testid="account-password-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#8C7B6B]" /> {hasPassword ? "Password sign-in enabled" : "Add a password"}
          </p>
          <p className="text-xs text-[#6E675F] mt-1.5">
            {hasPassword ? "Change the password you use alongside Google sign-in." : "Sign in with your email and a password even when Google isn't available."}
          </p>
        </div>
        {!open && (
          <button type="button" data-testid="account-password-toggle" onClick={() => setOpen(true)} className="text-xs tracking-[0.1em] uppercase font-semibold text-white bg-[#8C7B6B] hover:bg-[#75665A] rounded-none px-4 py-2.5 transition-colors">
            {hasPassword ? "Change password" : "Set password"}
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 mt-4" data-testid="account-password-form">
          {hasPassword && (
            <div>
              <Label htmlFor="pw-current" className="text-xs">Current password</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="account-password-current" className="mt-1" />
            </div>
          )}
          <div>
            <Label htmlFor="pw-new" className="text-xs">New password</Label>
            <Input id="pw-new" type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="account-password-new" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="pw-confirm" className="text-xs">Confirm password</Label>
            <Input id="pw-confirm" type="password" autoComplete="new-password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="account-password-confirm" className="mt-1" />
          </div>
          {error && <p className="sm:col-span-3 text-xs text-red-600" data-testid="account-password-error">{error}</p>}
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" disabled={busy} data-testid="account-password-submit" className="text-xs tracking-[0.1em] uppercase font-semibold text-white bg-[#1A1A1A] hover:bg-[#333] rounded-none px-5 py-2.5 transition-colors disabled:opacity-60">
              {busy ? "Saving…" : "Save password"}
            </button>
            <button type="button" data-testid="account-password-cancel" onClick={() => { setOpen(false); setError(""); setForm(EMPTY); }} className="text-xs tracking-[0.1em] uppercase font-semibold text-[#6E675F] hover:text-[#1A1A1A] px-4 py-2.5 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
